import { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import { MobileDevice, SyncData } from '../../../../types';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import UserDeviceDialog from '../../user-management/user-details/devices/device-details-dialog';
import DeleteUserDeviceDialog from '../../user-management/user-details/devices/device-delete-dialog';
import ApproveUserDeviceDialog from '../../user-management/user-details/devices/device-approve-dialog';
import BlockUserDeviceDialog from '../../user-management/user-details/devices/device-block-dialog';
import WipeUserDeviceDialog from '../../user-management/user-details/devices/device-wipe-dialog';
import WipeAccountUserDeviceDialog from '../../user-management/user-details/devices/device-account-wipe-dialog';
import MobileManagementToolbar from '../../user-management/user-details/devices/mobile-management-toolbar';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import { useUserSettings } from '../../../utils/use-user-settings';
import { useMutation } from '@tanstack/react-query';
import { exportMobileDevices } from '../../../api/users-api';
import { useStore } from '../../state/zustand-store';
import { handleErrors } from '../../../utils/errors';
import ChangeLogDialog from '../../../shared/change-log-dialog';

import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Android from '@mui/icons-material/Android';
import Apple from '@mui/icons-material/Apple';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import DeleteSweep from '@mui/icons-material/DeleteSweep';
import DoNotDisturb from '@mui/icons-material/DoNotDisturb';
import FolderDelete from '@mui/icons-material/FolderDelete';
import Google from '@mui/icons-material/Google';
import Pending from '@mui/icons-material/Pending';
import PhoneIphoneRounded from '@mui/icons-material/PhoneIphoneRounded';
import Smartphone from '@mui/icons-material/Smartphone';

interface MobileManagementGridProps {
  devices: MobileDevice[] | undefined;
  isLoading: boolean;
  toolsRoles: string[];
  changeLogRoles: string[];
  fetched: boolean;
}

export default function MobileManagementGrid({
  devices,
  isLoading,
  toolsRoles,
  changeLogRoles,
  fetched,
}: MobileManagementGridProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const { viewType } = useUserSettings();
  const [openDeleteDevice, setOpenDeleteDevice] = useState(false);
  const [openBlockDevice, setOpenBlockDevice] = useState(false);
  const [openApproveDevice, setOpenApproveDevice] = useState(false);
  const [openWipeDevice, setOpenWipeDevice] = useState(false);
  const [openWipeAccountDevice, setOpenWipeAccountDevice] = useState(false);
  const [openViewDeviceDetails, setOpenViewDeviceDetails] = useState(false);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<MobileDevice>();
  const [openExportChangeLog, setOpenExportChangeLog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate: handleExportMutate, isPending: isExporting } = useMutation({
    mutationFn: ({ changeLog }: { changeLog: string }) =>
      exportMobileDevices(changeLog),
    onSuccess: (spreadsheetId) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Mobile devices has been successfully exported.',
      });
      window.open(`https://docs.google.com/spreadsheets/d/${spreadsheetId}`);
      setOpenExportChangeLog(false);
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: 'There was an issue exporting mobile devices.',
      });
    },
  });

  const columns: GridColDef<MobileDevice>[] = useMemo(
    () => [
      {
        field: 'model',
        headerName: 'Device Name',
        flex: 2,
        pinnable: false,
        valueGetter: (value, row) => {
          return `${row.name[0].split(' ')[0]}'s ${value}`;
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 2,
        pinnable: false,
        valueGetter: (value) => {
          return value[0];
        },
      },
      {
        field: 'serialNumber',
        headerName: 'Serial Number',
        flex: 1,
        pinnable: false,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 2,
        pinnable: false,
        valueGetter: (value) => {
          return value[0];
        },
      },
      {
        field: 'os',
        headerName: 'OS',
        flex: 1,
        pinnable: false,
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 20,
        pinnable: false,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: [
          { label: 'iOS', value: 'IOS_SYNC' },
          { label: 'Android', value: 'ANDROID' },
          { label: 'Google', value: 'GOOGLE_SYNC' },
        ],
        renderCell: (params) => {
          if (params.value === 'IOS_SYNC') {
            return (
              <Tooltip title={params.value}>
                <Apple
                  key={params.value}
                  fontSize="small"
                  sx={{ color: '#7D7D7D', width: 'fit-content' }}
                />
              </Tooltip>
            );
          } else if (params.value === 'ANDROID') {
            return (
              <Tooltip title={params.value}>
                <Android
                  key={params.value}
                  fontSize="small"
                  sx={{ color: '#A4C639' }}
                />
              </Tooltip>
            );
          } else if (params.value === 'GOOGLE_SYNC') {
            return (
              <Tooltip title={params.value}>
                <Google
                  key={params.value}
                  fontSize="small"
                  sx={{ color: '#4285F4' }}
                />
              </Tooltip>
            );
          } else {
            return (
              <Tooltip title={params.value}>
                <Smartphone
                  key={params.value}
                  fontSize="small"
                  sx={{ color: 'disabled' }}
                />
              </Tooltip>
            );
          }
        },
      },
      {
        field: 'isForCompanyUseOnly',
        headerName: 'Ownership',
        type: 'singleSelect',
        valueOptions: [
          { label: 'Company owned', value: 'Company Owned' },
          { label: 'User owned', value: 'User Owned' },
        ],
        width: 130,
        pinnable: false,
        valueGetter: (value: boolean) => {
          if (value) {
            return 'Company Owned';
          } else {
            return 'User Owned';
          }
        },
      },
      {
        field: 'firstSync',
        headerName: 'First Sync',
        type: 'date',
        width: 110,
        pinnable: false,
        valueGetter: (value: SyncData) => {
          const date = new Date(value.value);
          return date;
        },
        valueFormatter: (value?: Date) => {
          if (value) {
            return value?.toLocaleDateString();
          } else {
            return 'N/A';
          }
        },
      },
      {
        field: 'lastSync',
        headerName: 'Last Sync',
        type: 'date',
        width: 110,
        pinnable: false,
        valueGetter: (value: SyncData) => {
          const date = new Date(value.value);
          return date;
        },
        valueFormatter: (value?: Date) => {
          if (value) {
            return value?.toLocaleDateString();
          } else {
            return 'N/A';
          }
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        pinnable: false,
        type: 'singleSelect',
        valueOptions: [
          { label: 'approved', value: 'APPROVED' },
          { label: 'blocked', value: 'BLOCKED' },
          { label: 'accounted wiped', value: 'UNKNOWN' },
          { label: 'wiped', value: 'WIPED' },
          { label: 'pending', value: 'PENDING' },
        ],
        renderCell: (params) => {
          const status = params.value || 'UNKNOWN';

          if (status === 'APPROVED') {
            return (
              <Chip
                variant="outlined"
                icon={<Check />}
                label="approved"
                color="success"
                size="small"
              />
            );
          } else if (status === 'BLOCKED') {
            return (
              <Chip
                variant="outlined"
                icon={<DoNotDisturb />}
                label={'blocked'}
                color={'warning'}
                size="small"
              />
            );
          } else if (status === 'UNKNOWN') {
            return (
              <Chip
                variant="outlined"
                icon={<FolderDelete />}
                label={'account wiped'}
                color={'error'}
                size="small"
              />
            );
          } else if (status === 'WIPED') {
            return (
              <Chip
                variant="outlined"
                icon={<DeleteSweep />}
                label={'wiped'}
                size="small"
                color="error"
              />
            );
          } else if (status === 'PENDING') {
            return (
              <Chip
                variant="outlined"
                icon={<Pending />}
                label={'pending'}
                size="small"
                color="warning"
              />
            );
          } else {
            return (
              <Chip
                variant="outlined"
                icon={<Close />}
                label={params.value.toLowerCase()}
                size="small"
                color="info"
              />
            );
          }
        },
      },
    ],
    []
  );

  const selectedDevices = useMemo(() => {
    return (
      devices?.filter(
        (device) => selectedDeviceIds?.includes(device.resourceId)
      ) || []
    );
  }, [devices, selectedDeviceIds]);

  const toolbarProps = useMemo(
    () => ({
      selectedDevices,
      toolsRoles,
      setOpenDeleteDevice,
      setOpenApproveDevice,
      setOpenBlockDevice,
      setOpenWipeDevice,
      setOpenWipeAccountDevice,
      handleExportMutate,
      isExporting,
      setOpenExportChangeLog,
      changeLogRoles,
    }),
    [selectedDevices, toolsRoles, devices, changeLogRoles, isExporting]
  );
  return (
    <>
      <SharedDataGridClient
        checkboxSelection
        selectedRows={selectedDeviceIds}
        setSelectedRows={setSelectedDeviceIds}
        getRowId={(row) => row.resourceId}
        defaultGridState={{
          columns: {
            columnVisibilityModel: {
              serialNumber: false,
            },
          },
        }}
        slots={{
          toolbar: useToolbar(MobileManagementToolbar, toolbarProps),
          ...createGridOverlays(
            !fetched ? 'Search Mobile Devices' : 'Mobile Devices',
            <PhoneIphoneRounded />,
            'Apply one or more filters to search for mobile devices.'
          ),
        }}
        disableRowSelectionOnClick
        rows={devices || []}
        onRowClick={(params) => {
          setSelectedDevice(params.row);
          setOpenViewDeviceDetails(true);
          createEvent(
            analytics.devices.mobile_management.viewed_mobile_device,
            {
              module: ModuleList.modules.devices.name,
              section: ModuleList.modules.devices.sections.mobileManagement,
            }
          );
        }}
        columns={columns}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        loading={isLoading}
        gridKey={viewType.mobileManagementIndexedGridStates?.activeGridState}
        gridStateKey="mobileManagementGridStates"
      />

      {openViewDeviceDetails && selectedDevice ? (
        <UserDeviceDialog
          open={openViewDeviceDetails}
          handleClose={() => setOpenViewDeviceDetails(false)}
          selectedDevice={selectedDevice}
        />
      ) : null}
      {openDeleteDevice && selectedDevices.length ? (
        <DeleteUserDeviceDialog
          open={openDeleteDevice}
          handleClose={() => setOpenDeleteDevice(false)}
          selectedDevices={selectedDevices}
          changeLogRoles={changeLogRoles}
          setSelectedDeviceIds={setSelectedDeviceIds}
        />
      ) : null}
      {openApproveDevice && selectedDevices.length ? (
        <ApproveUserDeviceDialog
          open={openApproveDevice}
          handleClose={() => setOpenApproveDevice(false)}
          selectedDevices={selectedDevices}
          changeLogRoles={changeLogRoles}
          setSelectedDeviceIds={setSelectedDeviceIds}
        />
      ) : null}
      {openBlockDevice && selectedDevices.length ? (
        <BlockUserDeviceDialog
          open={openBlockDevice}
          handleClose={() => setOpenBlockDevice(false)}
          selectedDevices={selectedDevices}
          changeLogRoles={changeLogRoles}
          setSelectedDeviceIds={setSelectedDeviceIds}
        />
      ) : null}
      {openWipeDevice && selectedDevices.length ? (
        <WipeUserDeviceDialog
          open={openWipeDevice}
          handleClose={() => setOpenWipeDevice(false)}
          selectedDevices={selectedDevices}
          changeLogRoles={changeLogRoles}
          setSelectedDeviceIds={setSelectedDeviceIds}
        />
      ) : null}
      {openWipeAccountDevice && selectedDevices.length ? (
        <WipeAccountUserDeviceDialog
          open={openWipeAccountDevice}
          handleClose={() => setOpenWipeAccountDevice(false)}
          selectedDevices={selectedDevices}
          changeLogRoles={changeLogRoles}
          setSelectedDeviceIds={setSelectedDeviceIds}
        />
      ) : null}
      <ChangeLogDialog
        open={openExportChangeLog}
        handleClose={() => setOpenExportChangeLog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() =>
          handleExportMutate({
            changeLog: changeLogMessage,
          })
        }
        mutateLoading={isExporting}
      />
    </>
  );
}
